import { useRoutes } from '@folklore/routes';
import {
    useQuery,
    useQueryClient,
    keepPreviousData as previousPlaceholder,
    QueryClient,
} from '@tanstack/react-query';
import { create, windowScheduler } from '@yornaath/batshit';
import { useRoute } from 'wouter';

import Api from '../lib/api/Api';

import { useApi } from '../contexts/ApiContext';
import { useIsIdle } from '../contexts/IdleContext';

export function fetchDocument(queryClient: QueryClient, api: Api, slug) {
    return queryClient.fetchQuery<FullDocument>({
        queryKey: ['document', slug, { snippetOnly: null }],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.findBySlug(queryParam, { signal }),
    });
}

export function prefetchDocument(queryClient: QueryClient, api: Api, slug) {
    return queryClient.prefetchQuery<FullDocument>({
        queryKey: ['document', slug, { snippetOnly: null }],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.findBySlug(queryParam, { signal }),
    });
}

export function usePrefetchDocument() {
    const api = useApi();
    const queryClient = useQueryClient();
    return (slug) => prefetchDocument(queryClient, api, slug);
}

let globalProgressBatcher = null;
function getGlobalProgressBatcher(api) {
    if (!globalProgressBatcher) {
        globalProgressBatcher = create<number, number>({
            fetcher: (ids) =>
                api.documents.progress({
                    id: ids,
                }),
            resolver: (progressList, id) => progressList[id] || null,
            scheduler: windowScheduler(1000),
        });
    }
    return globalProgressBatcher;
}

export function useDocumentProgress(id: string | null, opts: { enabled?: boolean } = {}) {
    const { enabled = true, ...queryOpts } = opts || {};
    const api = useApi();
    const idle = useIsIdle();
    const progressBatcher = getGlobalProgressBatcher(api);

    const { data: progress = null, ...queryResult } = useQuery<number>({
        queryKey: ['document_progress', id],
        queryFn: ({ queryKey: [, id] }) => progressBatcher.fetch(id),
        enabled: enabled && id !== null && !idle,
        ...queryOpts,
    });

    return {
        progress,
        ...queryResult,
    };
}

type UseDocumentOptions<T> = Omit<
    Parameters<
        typeof useQuery<T | null, Error, T | null, [string, string, { snippetOnly?: boolean }]>
    >[0],
    'queryKey' | 'queryFn'
> & {
    snippetOnly?: boolean;
    keepPreviousData?: boolean;
};

export default function useDocument<D = FullDocument>(
    slug: string,
    opts: UseDocumentOptions<D> = null,
) {
    const api = useApi();
    const {
        keepPreviousData = true,
        snippetOnly = null,
        enabled = true,
        ...queryOpts
    } = opts || {};
    const {
        data: document = null,
        isFetched = false,
        ...queryResult
    } = useQuery({
        queryKey: ['document', slug, { snippetOnly }],
        queryFn: ({ queryKey: [, queryParam, other], signal }) =>
            api.documents.findBySlug(queryParam, { signal, ...other }),
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        enabled: enabled && slug !== null,
        ...queryOpts,
    });

    return {
        document,
        notFound: document === null && isFetched,
        isFetched,
        ...queryResult,
    };
}

export function useCurrentDocument(): FullDocument | null {
    const routes = useRoutes();
    const [, documentParams] = useRoute(routes.document);
    const [, previewParams] = useRoute(routes.preview);
    const { slug: documentSlug = null } = documentParams || {};
    const { token: previewToken = null } = previewParams || {};
    const { document = null } = useDocument(documentSlug, {
        enabled: documentSlug !== null,
    });
    const { document: previewDocument = null } = useDocumentPreview(previewToken, {
        enabled: previewToken !== null,
    });
    return previewDocument || document;
}

export function fetchDocumentPreview(queryClient, api, token) {
    return queryClient.fetchQuery({
        queryKey: ['document_preview', token],
        queryFn: ({ queryKey: [, tokenParam], signal }) =>
            api.documents.findPreviewToken(tokenParam, { signal }),
    });
}

type UseDocumentPreviewOptions = Omit<
    Parameters<typeof useQuery<FullDocument>>[0],
    'queryKey' | 'queryFn'
> & {
    snippetOnly?: boolean;
    keepPreviousData?: boolean;
};

export function useDocumentPreview(token: string, opts: UseDocumentPreviewOptions = null) {
    const api = useApi();
    const { keepPreviousData = true, enabled = true, ...queryOpts } = opts || {};
    const {
        data: document = null,
        isFetched = false,
        ...queryResult
    } = useQuery({
        queryKey: ['document_preview', token],
        queryFn: ({ queryKey: [, tokenParam], signal }) =>
            api.documents.findPreviewToken(tokenParam, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        enabled: enabled && token !== null,
        ...queryOpts,
    });

    return {
        document,
        notFound: document === null && isFetched,
        isFetched,
        ...queryResult,
    };
}

export function useDocumentBlockData(id, blockId, opts = null) {
    const api = useApi();
    const { keepPreviousData = true, enabled = true, ...queryOpts } = opts || {};
    const {
        data = null,
        isFetched = false,
        ...queryResult
    } = useQuery({
        queryKey: ['document_data', id, blockId],
        queryFn: ({ queryKey: [, idParam, blockIdParam], signal }) =>
            api.documents.getBlockData(idParam, blockIdParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        enabled: enabled && id !== null && blockId !== null,
        ...queryOpts,
    });

    return {
        data,
        notFound: data === null && isFetched,
        isFetched,
        ...queryResult,
    };
}

export function useDocumentSnippet(slug, opts: UseDocumentOptions<DocumentSnippet> = null) {
    return useDocument<DocumentSnippet>(slug, { ...(opts || {}), snippetOnly: true });
}
